import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { EVENT_CATEGORIES } from "@/lib/i18n";
import { MapPin, Calendar, Clock, Users, Globe, Ticket, QrCode, Plus } from "lucide-react";
import { FORMAT_LABEL } from "@/components/events/EventFormatFields";
import { useAuth } from "@/context/AuthContext";
import { FavoriteButton } from "@/components/FavoriteButton";
import { FeaturedBadge } from "@/components/featured/FeaturedBadge";
import { useFeaturedImpression, featuredClickProps } from "@/components/featured/featuredMetrics";

export default function Events() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "";
  const city = params.get("city") || "";
  const [events, setEvents] = useState([]);
  const [cities, setCities] = useState([]);

  useEffect(() => { api.get("/cities").then(r => setCities(r.data)); }, []);
  useEffect(() => {
    const q = {};
    if (category) q.category = category;
    if (city) q.city = city;
    api.get("/events", { params: q }).then(r => setEvents(r.data));
  }, [category, city]);

  const setP = (k, v) => {
    const p = new URLSearchParams(params);
    v ? p.set(k, v) : p.delete(k);
    setParams(p);
  };

  return (
    <div className="page fade-up">
      <div className="pt-4 pb-2 flex items-center justify-between">
        <h1 className="font-display text-2xl font-extrabold">{t("events")}</h1>
        <div className="flex items-center gap-2">
        {!user && <Link to="/publicar?type=event" data-testid="events-publish-btn"
              className="text-xs font-semibold px-3 py-2 rounded-full bg-[#c8102e] text-white hover:bg-[#a50c24] transition-colors">
          + {t("publish_event")}
        </Link>}
        </div>
      </div>

      {user && (
        <div className="flex flex-wrap gap-2 pb-2 mb-1" data-testid="events-quick-links">
          <Link to="/publicar?type=event" data-testid="events-publish-btn" className="chip whitespace-nowrap !text-[11px] !bg-[#c8102e] !text-white !border-[#c8102e]"><Plus size={11} className="inline mr-1" />Criar evento</Link>
          <Link to="/eventos/organizador" data-testid="events-organizer-link" className="chip whitespace-nowrap !text-[11px]"><Calendar size={11} className="inline mr-1" />Os meus eventos</Link>
          <Link to="/eventos/organizador" data-testid="events-link-manage-tickets" className="chip whitespace-nowrap !text-[11px]"><Ticket size={11} className="inline mr-1" />Gerir bilhetes</Link>
          <Link to="/bilhetes" data-testid="events-my-tickets-link" className="chip whitespace-nowrap !text-[11px]"><QrCode size={11} className="inline mr-1" />Os meus bilhetes</Link>
        </div>
      )}

      <div className="flex overflow-x-auto gap-2 pb-2 -mx-4 px-4 scrollbar-none mb-2">
        <button onClick={() => setP("category", "")} className="chip whitespace-nowrap" data-active={!category}>
          {t("all_categories")}
        </button>
        {EVENT_CATEGORIES.map(c => (
          <button key={c} onClick={() => setP("category", c)} data-testid={`events-cat-${c}`}
                  className="chip whitespace-nowrap" data-active={category === c}>
            {c}
          </button>
        ))}
      </div>

      <div className="flex overflow-x-auto gap-2 pb-2 -mx-4 px-4 scrollbar-none mb-4">
        <button onClick={() => setP("city", "")} className="chip whitespace-nowrap" data-active={!city}>
          {t("all_cities")}
        </button>
        {cities.map(c => (
          <button key={c.name} onClick={() => setP("city", c.name)} className="chip whitespace-nowrap" data-active={city === c.name}>
            {c.name}
          </button>
        ))}
      </div>

      <div className="space-y-3 mb-8">
        {events.length === 0 && (
          <p className="text-sm text-zinc-500 italic text-center py-8">Nenhum evento encontrado.</p>
        )}
        {events.map(e => <EventCard key={e.id} e={e} lang={lang} />)}
      </div>
    </div>
  );
}

function EventCard({ e, lang }) {
  const ref = useFeaturedImpression("event", e.id, !!e.featured_active);
  return (
    <Link ref={ref} to={`/eventos/${e.id}`} data-testid={`event-item-${e.id}`} data-featured={e.featured_active ? "1" : "0"} {...featuredClickProps(e, "event")}
          className="block bg-white rounded-2xl border border-zinc-200 overflow-hidden card-hover">
      <div className="relative aspect-[16/9] bg-zinc-100">
        {e.image_url && <img src={e.image_url} alt={e.name} className="w-full h-full object-cover" />}
        <span className="absolute top-2 left-2 chip !py-1 !bg-black !text-white !border-black">{e.category}</span>
        {e.featured_active && <FeaturedBadge onDark className="absolute top-2 right-2" testid={`featured-badge-event-${e.id}`} />}
      </div>
      <div className="p-4">
        <h3 className="font-display font-bold text-base leading-tight mb-2 flex items-start justify-between gap-2"><span>{e.name}</span><FavoriteButton type="event" id={e.id} /></h3>
        <div className="flex items-center gap-3 text-xs text-zinc-500 flex-wrap">
          <span className="flex items-center gap-1"><Calendar size={12} /> {new Date(e.date).toLocaleDateString(lang === "pt" ? "pt-PT" : "en-GB")}</span>
          <span className="flex items-center gap-1"><Clock size={12} /> {e.time}</span>
          <span className="flex items-center gap-1">{e.format === "online" ? <Globe size={12} /> : <MapPin size={12} />} {e.format === "online" ? "Online" : e.city}</span>
          {e.format && e.format !== "in_person" && <span className="chip !py-0 !px-2 !text-[10px]" data-testid={`event-format-${e.id}`}>{FORMAT_LABEL[e.format]}</span>}
          {e.rsvp_enabled && e.going_count > 0 && <span className="flex items-center gap-1" data-testid={`event-going-${e.id}`}><Users size={12} /> {e.going_count} vão</span>}
        </div>
      </div>
    </Link>
  );
}
